import React, { useEffect, useState, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useDispatch } from 'react-redux';
import { fetchFilteredArtistTracks, fetchTrackMixAppearances } from '../../redux/thunk/djpalThunk';
import DJPalScrollableList from './DJPalScrollableList';
import MultiArtistText from '../MultiArtistText';
import { entityId } from '../../utils/entityId';
import PlayAllButton from '../common/PlayAllButton';
import { useIsMobileWeb } from '../../hooks/useIsMobileWeb';
import MobileListSearch from '../common/MobileListSearch';
import CoverArt from '../CoverArt';

// Single-column track list modal.
//
// Mirrors ArtistsListModal: a row per track, each expandable to a list of
// mixes. Used for both "Tracks they played" (relation='played' — mixes are
// scoped to the subject artist as DJ) and "Their tracks played by others"
// (relation='by' — mixes are all DJs who played the track).
const TracksListModal = ({
    title,
    subtitle,
    artistName,
    artistPublicId,
    relation,
    dateRange,
    initialTracks = [],
    initialPagination = null,
    totalCount = null,
    onTrackClick,
    onMixClick,
    onArtistClick,
    onClose,
}) => {
    const dispatch = useDispatch();
    const [tracks, setTracks] = useState(initialTracks);
    const [pagination, setPagination] = useState(initialPagination);
    const [loadingMore, setLoadingMore] = useState(false);
    const [filter, setFilter] = useState('');
    const isMobileWeb = useIsMobileWeb();
    const [filterLoading, setFilterLoading] = useState(false);
    const [expandedId, setExpandedId] = useState(null);
    const [mixesByTrack, setMixesByTrack] = useState({}); // id → { mixes, total, loading, error }
    const [loadError, setLoadError] = useState(null);
    const didInitialFetchRef = useRef(false);

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose]);

    // Drive the list from the same endpoint that handles pagination, so
    // the modal's load-more results align with what's on screen. The
    // parent-supplied `initialTracks` is shown for the first paint, then
    // replaced when the fetch lands.
    //
    // Important: on transient errors (timeout, server hiccup), we keep
    // whatever tracks were already loaded. Wiping the list on every error
    // was making a single failed page-2 fetch look like a hard reset.
    useEffect(() => {
        const q = filter.trim();
        // Skip the very first run when there's no filter — the initial
        // fetch already happens in its own effect below. This avoids a
        // double-fetch on mount (and a StrictMode double-clear).
        if (!q && !didInitialFetchRef.current) return undefined;
        let cancelled = false;
        setFilterLoading(true);
        const timer = setTimeout(async () => {
            try {
                const result = await dispatch(fetchFilteredArtistTracks({
                    relation, artistName, artistPublicId,
                    query: q || undefined,
                    page: 1,
                })).unwrap();
                if (cancelled) return;
                setTracks(result.tracks || []);
                setPagination(result.pagination || null);
                setLoadError(null);
            } catch (err) {
                if (cancelled) return;
                console.error('Filter tracks failed:', err);
                setLoadError(err?.message || 'Failed to load tracks');
                // Only blank the list when the user is actively filtering —
                // a failed filter query implies "no matches for this term".
                // For an empty-filter background refetch, keep existing data.
                if (q) {
                    setTracks([]);
                    setPagination(null);
                }
            } finally {
                if (!cancelled) setFilterLoading(false);
            }
        }, q ? 250 : 0);
        return () => { cancelled = true; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter, relation, artistName, artistPublicId]);

    // One-shot initial fetch on mount. Separate from the filter effect so
    // it can't be re-triggered by harmless re-renders (and so a transient
    // failure on page 1 doesn't compound with later state updates).
    useEffect(() => {
        if (didInitialFetchRef.current) return undefined;
        didInitialFetchRef.current = true;
        let cancelled = false;
        setFilterLoading(true);
        (async () => {
            try {
                const result = await dispatch(fetchFilteredArtistTracks({
                    relation, artistName, artistPublicId,
                    query: undefined,
                    page: 1,
                })).unwrap();
                if (cancelled) return;
                setTracks(result.tracks || []);
                setPagination(result.pagination || null);
                setLoadError(null);
            } catch (err) {
                if (cancelled) return;
                console.error('Initial tracks fetch failed:', err);
                setLoadError(err?.message || 'Failed to load tracks');
            } finally {
                if (!cancelled) setFilterLoading(false);
            }
        })();
        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleLoadMore = useCallback(async () => {
        if (loadingMore || !pagination?.hasNext) return;
        setLoadingMore(true);
        setLoadError(null);
        try {
            const result = await dispatch(fetchFilteredArtistTracks({
                relation, artistName, artistPublicId,
                query: filter.trim() || undefined,
                page: (pagination.page || 1) + 1,
            })).unwrap();
            setTracks(prev => [...prev, ...(result.tracks || [])]);
            if (result.pagination) setPagination(result.pagination);
        } catch (err) {
            console.error('Load more tracks failed:', err);
            setLoadError(err?.message || 'Failed to load more tracks');
        } finally {
            setLoadingMore(false);
        }
    }, [dispatch, loadingMore, pagination, relation, artistName, artistPublicId, filter]);

    const handleToggleExpand = useCallback(async (track) => {
        const key = entityId(track) || `${track.artist}::${track.title}`;
        if (expandedId === key) { setExpandedId(null); return; }
        setExpandedId(key);
        if (mixesByTrack[key]?.mixes) return;
        setMixesByTrack(prev => ({ ...prev, [key]: { ...(prev[key] || {}), loading: true } }));
        try {
            const trackPublicId = entityId(track);
            const result = await dispatch(fetchTrackMixAppearances({
                trackPublicId,
                artistPublicId: relation === 'played' ? artistPublicId : undefined,
                artistName: relation === 'played' ? artistName : undefined,
                page: 1,
                limit: 50,
            })).unwrap();
            setMixesByTrack(prev => ({
                ...prev,
                [key]: {
                    mixes: result.mixes || [],
                    total: result.pagination?.totalCount ?? null,
                    loading: false,
                },
            }));
        } catch (err) {
            setMixesByTrack(prev => ({
                ...prev,
                [key]: { mixes: [], loading: false, error: err?.message || 'Failed to load' },
            }));
        }
    }, [expandedId, mixesByTrack, dispatch, relation, artistName, artistPublicId]);

    // Prefer the paginated endpoint's own totalCount over the parent-supplied
    // prop. The prop comes from a summary stat (`statsIncluded.totalTracksSupported`
    // for `'by'`) that counts only tracks where the artist is in artist_ids,
    // while the modal's endpoint also includes remixer_artist_ids matches — so
    // the two can disagree (e.g. 175 vs 211 for an artist with 36 remixes).
    const total = pagination?.totalCount ?? totalCount ?? tracks.length;
    const isFiltering = !!filter.trim();

    return ReactDOM.createPortal(
        <div className="djpal-modal-backdrop" onMouseDown={onClose}>
            <div className="djpal-modal djpal-artists-list-modal djpal-tracks-list-modal" onMouseDown={e => e.stopPropagation()}>
                <div className="djpal-modal-header">
                    <div className="djpal-modal-title-group">
                        <h3 className="djpal-modal-title">{title}</h3>
                        {subtitle && <div className="djpal-modal-subtitle">{subtitle}</div>}
                    </div>
                    {tracks.length > 0 && (
                        <PlayAllButton tracks={tracks} from="tracks_modal" />
                    )}
                    <button className="djpal-modal-close" onClick={onClose} aria-label="Close">×</button>
                </div>
                <div className="djpal-modal-body">
                    {isMobileWeb ? (
                        <div className="djpal-artists-list-modal-search djpal-artists-list-modal-search--mobile">
                            <MobileListSearch value={filter} onChange={setFilter} placeholder="Filter tracks" ariaLabel="Filter tracks" />
                        </div>
                    ) : (
                        <div className="djpal-artists-list-modal-search">
                            <input
                                type="text"
                                placeholder="Filter tracks…"
                                value={filter}
                                onChange={(e) => setFilter(e.target.value)}
                                aria-label="Filter tracks"
                            />
                            {filter && (
                                <button type="button" className="djpal-artists-list-modal-search-clear" onClick={() => setFilter('')} aria-label="Clear filter">×</button>
                            )}
                        </div>
                    )}
                    {filterLoading && tracks.length === 0 ? (
                        <div className="djpal-modal-skel" aria-label="Loading">
                            {[...Array(8).keys()].map((i) => (
                                <div key={i} className="djpal-modal-skel-row">
                                    <span className="djpal-modal-skel-art" />
                                    <span className="djpal-modal-skel-text">
                                        <span className="djpal-modal-skel-line" />
                                        <span className="djpal-modal-skel-line short" />
                                    </span>
                                </div>
                            ))}
                        </div>
                    ) : tracks.length === 0 ? (
                        <div className="djpal-empty-hint">
                            {loadError ? `Couldn't load tracks: ${loadError}` : (isFiltering ? `No matches for "${filter.trim()}"` : 'No tracks found.')}
                        </div>
                    ) : (
                        <DJPalScrollableList
                            maxHeight="55vh"
                            onScrollBottom={pagination?.hasNext ? handleLoadMore : undefined}
                        >
                            {tracks.map((t, i) => {
                                const key = entityId(t) || `${t.artist}::${t.title}::${i}`;
                                const expanded = expandedId === key;
                                const detail = mixesByTrack[key];
                                const art = t.artworkUrl || t.artwork || t.image || '';
                                const label = t.artist ? `${t.artist} – ${t.title}` : t.title;
                                // Per-row subtitle: prefer a server-provided
                                // mix count when present; otherwise omit.
                                const playCount = t.mixCount ?? t.mixesCount ?? t.playCount ?? null;
                                const sub = playCount != null
                                    ? `played in ${playCount} ${playCount === 1 ? 'mix' : 'mixes'}`
                                    : null;
                                return (
                                    <div key={key} className={`djpal-artists-list-modal-row${expanded ? ' is-expanded' : ''}`}>
                                        <div
                                            className="djpal-artists-list-modal-head clickable"
                                            onClick={() => handleToggleExpand(t)}
                                        >
                                            {art ? (
                                                <CoverArt className="artist-avatar" src={art} size={24} />
                                            ) : (
                                                <div className="artist-avatar">&#9835;</div>
                                            )}
                                            <div className="djpal-artists-list-modal-meta">
                                                <span className="djpal-artists-list-modal-name" title={label}>{t.title}</span>
                                                {(t.artist || (t.artists && t.artists.length > 0)) && (
                                                    <span className="djpal-artists-list-modal-sub" title={t.artist}>
                                                        {onArtistClick ? (
                                                            <MultiArtistText
                                                                artists={t.artists}
                                                                fallbackText={t.artist}
                                                                segmentClassName="clickable-artist"
                                                                onSelect={(a) => { onArtistClick(a); onClose(); }}
                                                            />
                                                        ) : t.artist}
                                                        {sub ? ` · ${sub}` : ''}
                                                    </span>
                                                )}
                                                {!t.artist && !(t.artists && t.artists.length > 0) && sub && (
                                                    <span className="djpal-artists-list-modal-sub">{sub}</span>
                                                )}
                                            </div>
                                            <span className="djpal-artists-list-modal-chevron" aria-hidden>{expanded ? '▾' : '▸'}</span>
                                        </div>
                                        {expanded && (
                                            <div className="djpal-artists-list-modal-mixes djpal-artists-list-modal-mixes--flush">
                                                {detail?.loading && (
                                                    <div className="djpal-load-more-spinner"><span className="mix-play-spinner" /></div>
                                                )}
                                                {detail?.error && (
                                                    <div className="djpal-empty-hint">Couldn't load mixes: {detail.error}</div>
                                                )}
                                                {!detail?.loading && !detail?.error && detail?.mixes?.length === 0 && (
                                                    <div className="djpal-empty-hint">No mixes found.</div>
                                                )}
                                                {detail?.mixes?.map((mx, mi) => {
                                                    const djLabel = mx.djName || mx.artist || '';
                                                    const mixLabel = djLabel
                                                        ? `${djLabel} – ${mx.title || mx.eventName || ''}`
                                                        : (mx.title || mx.eventName || '(untitled mix)');
                                                    return (
                                                        <div
                                                            key={`${key}-mx-${mi}`}
                                                            className="djpal-artists-list-modal-mix clickable"
                                                            onClick={(e) => { e.stopPropagation(); onMixClick?.(mx); onClose(); }}
                                                            title={mixLabel}
                                                        >
                                                            {mx.date && <span className="djpal-artists-list-modal-mix-date">{mx.date}</span>}
                                                            <span className="djpal-artists-list-modal-mix-label">{mixLabel}</span>
                                                        </div>
                                                    );
                                                })}
                                                {detail?.mixes?.length > 0 && onTrackClick && (
                                                    <div
                                                        className="djpal-artists-list-modal-mix clickable djpal-artists-list-modal-track-link"
                                                        onClick={(e) => { e.stopPropagation(); onTrackClick(t); onClose(); }}
                                                    >
                                                        <span className="djpal-artists-list-modal-mix-label">View track page →</span>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                            {loadingMore && (
                                <div className="djpal-load-more-spinner"><span className="mix-play-spinner" /></div>
                            )}
                            {!loadingMore && loadError && tracks.length > 0 && (
                                <div className="djpal-empty-hint">Couldn't load more: {loadError}</div>
                            )}
                        </DJPalScrollableList>
                    )}
                </div>
                <div className="djpal-modal-footer">
                    {isFiltering
                        ? `${tracks.length}${pagination?.totalCount != null ? ` of ${pagination.totalCount}` : ''} matching`
                        : `${tracks.length}${total != null && total !== tracks.length ? ` of ${total}` : ''} ${total === 1 ? 'track' : 'tracks'}`}
                </div>
            </div>
        </div>,
        document.body,
    );
};

export default TracksListModal;
